import { useEffect, useRef, useState } from "react";
import {
  type Api,
  ApiError,
  type EditorPage,
  type PageVersionSummary,
} from "../api";

/**
 * Published-version history for the selected page (server-side, backed by
 * page_versions — distinct from the in-session Undo/Redo). Restore brings a
 * version back AS THE DRAFT; the normal preview/publish flow ships it.
 */
export function PageVersionHistory({
  api,
  pageId,
  disabled,
  onRestored,
}: {
  api: Api;
  pageId: string;
  disabled: boolean;
  onRestored: (page: EditorPage, versionId: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<PageVersionSummary[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setOpen(false);
    setVersions(null);
    setError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && versions === null) {
      try {
        const { versions: rows } = await api.pageVersions(pageId);
        setVersions(rows);
      } catch (caught) {
        setError(
          caught instanceof ApiError
            ? caught.message
            : "Could not load versions.",
        );
      }
    }
  };

  const restore = async (versionId: number) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { page } = await api.restorePageVersion(pageId, versionId);
      setOpen(false);
      setVersions(null);
      onRestored(page, versionId);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Could not restore.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-version-history" ref={rootRef}>
      <button
        className="btn-secondary"
        data-testid="page-version-history"
        disabled={disabled}
        onClick={toggle}
        title="Published versions of this page"
        type="button"
      >
        Versions
      </button>
      {open && (
        <div className="page-version-popover" role="menu">
          <p className="muted small">
            Every publish keeps a copy. Restoring loads it as your draft.
          </p>
          {error && <p className="error small">{error}</p>}
          {versions === null && !error && (
            <p className="muted small">Loading…</p>
          )}
          {versions?.length === 0 && (
            <p className="muted small">No published versions yet.</p>
          )}
          {versions && versions.length > 0 && (
            <ul>
              {versions.map((version) => (
                <li key={version.id}>
                  <span>
                    {new Date(version.createdAt).toLocaleString()}
                    <span className="muted small">
                      {" "}
                      · {version.blockCount} block
                      {version.blockCount === 1 ? "" : "s"}
                    </span>
                  </span>
                  <button
                    className="btn-secondary"
                    disabled={busy}
                    onClick={() => restore(version.id)}
                    type="button"
                  >
                    Restore
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
